import { Routes, Route } from "react-router-dom";
import { ROUTES } from "./routePaths";
import { Suspense, useState, useEffect } from "react";
import { checkNetworkStatus, subscribeToNetwork } from "../utils/networkManager";
import ProtectedRoute from "./ProtectedRoutes";
import PublicRoutes from "./PublicRoutes";
import Loader from "../components/Loader/Loader";
import ScrollToTop from "../components/ScrollToTop/ScrollToTop";
import NoInternet from "../components/NoInternet/NoInternet";
import LaunchScreen from "../containers/LaunchScreen/LaunchScreen.jsx";
import NotFound from "../containers/NotFound/NotFound.jsx";
import EnableGpsLocation from "../containers/Permissions/EnableGpsLocation.jsx";
import GoToSettingModal from "../containers/Permissions/GoToSettingModal.jsx";
import { lazyWithRetry } from "../utils/lazyWithRetry.js";

const HomeScreen = lazyWithRetry(() => import("../containers/HomeScreen/HomeScreen"));
const OnboardingHome = lazyWithRetry(() => import("../containers/OnboardingHome/OnboardingHome"));
const Permissions = lazyWithRetry(() => import("../containers/Permissions"));
const PermissionsDeclined = lazyWithRetry(
  () => import("../containers/Permissions/PermissionsDeclined.jsx")
);
const AppInitializer = lazyWithRetry(() => import("../containers/AppInitializer/AppInitializer"));
const CreditReport = lazyWithRetry(() => import("../containers/CreditReport/CreditReport"));
const CreditReportPayment = lazyWithRetry(
  () => import("../containers/CreditReport/CreditReportPayment/CreditReportPayment")
);
const CreditReportPaymentSuccess = lazyWithRetry(
  () => import("../containers/CreditReport/CreditReportPaymentSuccess/CreditReportPaymentSuccess")
);
const CreditReportPaymentApology = lazyWithRetry(
  () => import("../containers/CreditReport/CreditReportPaymentApology/CreditReportPaymentApology")
);
const Login = lazyWithRetry(() => import("../containers/Auth/Login"));
const LoginOTP = lazyWithRetry(() => import("../containers/Auth/LoginOTP"));
const RejectScreen = lazyWithRetry(() => import("../containers/CommonScreens/RejectScreen"));
const ManualReviewScreen = lazyWithRetry(
  () => import("../containers/CommonScreens/ManualReviewScreen")
);
const UnifiedKycManualScreen = lazyWithRetry(
  () => import("../containers/Boosters/OkycAuth/UnifiedKycManualScreen/index.js")
);
const EmailVerification = lazyWithRetry(() => import("../containers/EmailVerification"));
const PersonalDetailsForm = lazyWithRetry(() => import("../containers/PersonalDetails"));
const EligibilityCheckPending = lazyWithRetry(
  () => import("../containers/EligibilityCheck/EligibilityCheckPending")
);
const IncomeDetailsForm = lazyWithRetry(() => import("../containers/IncomeDetails"));
const AddressDetailsForm = lazyWithRetry(() => import("../containers/AddressDetails"));
const PanCheckPending = lazyWithRetry(
  () => import("../containers/PanCheck/PanCheckPending/PanCheckPending")
);
const PanCheckFailed = lazyWithRetry(
  () => import("../containers/PanCheck/PanCheckFailed/PanCheckFailed")
);
const ReferenceVerification = lazyWithRetry(() => import("../containers/ReferenceVerification"));
const KycVerification = lazyWithRetry(
  () => import("../containers/Kyc/KycVerification/KycVerification")
);
const VideoKyc = lazyWithRetry(() => import("../containers/Kyc/KycVerification/VideoKyc/VideoKyc"));
const UploadDocuments = lazyWithRetry(
  () => import("../containers/Kyc/KycVerification/UploadDocuments/UploadDocuments")
);
const ReuploadDocuments = lazyWithRetry(
  () => import("../containers/Kyc/ReuploadDocuments/ReuploadDocuments")
);
const KycCallbackPending = lazyWithRetry(
  () => import("../containers/Kyc/KycCallbackPending/KycCallbackPending")
);
const VkycStatusPending = lazyWithRetry(
  () => import("../containers/Kyc/VkycStatusPending/VkycStatusPending")
);
const Congratulations = lazyWithRetry(
  () => import("../containers/Congratulations/Congratulations")
);
const Stage3Boosters = lazyWithRetry(() => import("../containers/stage3Boosters"));
const NextSteps = lazyWithRetry(() => import("../containers/NextSteps"));
const MobileAuth = lazyWithRetry(() => import("../containers/Boosters/MobileAuth/MobileAuth"));
const HandleUnifiedKYC = lazyWithRetry(
  () => import("../containers/Boosters/OkycAuth/HandleUnifiedKYC")
);
const UnifiedKycWaiting = lazyWithRetry(
  () => import("../containers/Boosters/OkycAuth/UnifiedKycWaiting")
);
const HouseholdIncomeDeclaration = lazyWithRetry(
  () => import("../containers/Boosters/HouseholdIncomeDeclaration")
);
const AddressValidation = lazyWithRetry(
  () => import("../containers/Boosters/AddressValidationStage4/index.js")
);
const CkycConsent = lazyWithRetry(() => import("../containers/Boosters/CkycAuth/CkycConsent"));
const CkycDetails = lazyWithRetry(() => import("../containers/Boosters/CkycAuth/CkycDetails"));
const LoanPlanPage = lazyWithRetry(() => import("../containers/loan/LoanPlan"));
const LoanSummaryPage = lazyWithRetry(() => import("../containers/loan/LoanSummary"));
const CouponPage = lazyWithRetry(() => import("../containers/loan/Coupon"));
const LoanPurposePage = lazyWithRetry(() => import("../containers/loan/LoanPurpose"));
const ReEvaluation = lazyWithRetry(() => import("../containers/ReEvaluation/ReEvaluation"));
const AddBankAccountForm = lazyWithRetry(
  () => import("../containers/Bank/AddBankAccount/AddBankAccountForm")
);
const BankAccount = lazyWithRetry(() => import("../containers/Bank/BankAccounts/BankAccount"));
const PennyDropCheckPending = lazyWithRetry(
  () => import("../containers/Bank/PennyDropPending/PennyDropCheckPending")
);

const SignLoanAgreement = lazyWithRetry(
  () => import("../containers/LoanAgreement/SignLoanAgreement")
);
const EsignAgreement = lazyWithRetry(() => import("../containers/LoanAgreement/EsignAgreement"));
const EsignSelfie = lazyWithRetry(() => import("../containers/LoanAgreement/EsignSelfie"));
const LiveSelfieCapture = lazyWithRetry(
  () => import("../containers/LoanAgreement/LiveSelfieCapture")
);
const EsignFailed = lazyWithRetry(() => import("../containers/EsignStatusCheck/ESignFailed"));
const EsignManualVerification = lazyWithRetry(
  () => import("../containers/EsignStatusCheck/EsignManualVerification")
);
const LoanSanctionLetter = lazyWithRetry(
  () => import("../containers/LoanAgreement/LoanSanctionLetter")
);
const EsignInProgress = lazyWithRetry(
  () => import("../containers/EsignStatusCheck/EsignInProgress")
);
const DisbursalInProgress = lazyWithRetry(
  () => import("../containers/LoanDisbursal/LoanDisbursalInProgress")
);
const ENachSetup = lazyWithRetry(() => import("../containers/ENach/ENachSetup"));
const ENachStep = lazyWithRetry(() => import("../containers/ENach/ENachStep"));

// Stage 5 — Repayment
const RepaymentHome = lazyWithRetry(
  () => import("../containers/Repayment/RepaymentHome/RepaymentHome")
);
const RepaymentScheduleDetails = lazyWithRetry(
  () => import("../containers/Repayment/RepaymentScheduleDetails/RepaymentScheduleDetails")
);
const EmiDetails = lazyWithRetry(() => import("../containers/Repayment/EmiDetails/EmiDetails"));
const RepaymentSuccess = lazyWithRetry(
  () => import("../containers/Repayment/RepaymentSuccess/RepaymentSuccess")
);
const ClosedLoanSuccess = lazyWithRetry(
  () => import("../containers/Repayment/ClosedLoanSuccess/ClosedLoanSuccess")
);
const RepaymentFailed = lazyWithRetry(
  () => import("../containers/Repayment/RepaymentFailed/RepaymentFailed")
);
const PhonePeCallback = lazyWithRetry(
  () => import("../containers/Repayment/PhonePeCallback/PhonePeCallback")
);

const Profile = lazyWithRetry(() => import("../containers/Profile/Profile"));
const ManageProfile = lazyWithRetry(
  () => import("../containers/Profile/ManageProfile/ManageProfile")
);
const ContactUs = lazyWithRetry(() => import("../containers/Profile/ContactUs/ContactUs"));
const LoanHistory = lazyWithRetry(() => import("../containers/Profile/LoanHistory/LoanHistory"));
const DeactivateProfile = lazyWithRetry(
  () => import("../containers/Profile/ProfileDeactivate/DeactivateProfile/DeactivateProfile")
);
const DeactivateProfileOtp = lazyWithRetry(
  () => import("../containers/Profile/ProfileDeactivate/DeactivateProfileOtp/DeactivateProfileOtp")
);
const CantDeactivate = lazyWithRetry(
  () => import("../containers/Profile/ProfileDeactivate/CantDeactivate/CantDeactivate")
);
const CantDelete = lazyWithRetry(
  () => import("../containers/Profile/Profiledelete/CantDelete/CantDelete")
);
const DeleteProfile = lazyWithRetry(
  () => import("../containers/Profile/Profiledelete/DeleteProfile/DeleteProfile")
);
const DeleteProfileSuccess = lazyWithRetry(
  () => import("../containers/Profile/Profiledelete/DeleteProfileSuccess/DeleteProfileSuccess")
);
const DownloadLoanDocument = lazyWithRetry(
  () => import("../containers/Profile/DownloadLoanDocument/DownloadLoanDocument")
);
const ManagePermissions = lazyWithRetry(() => import("../containers/Profile/ManagePermissions"));
const ProfileBankAccount = lazyWithRetry(
  () => import("../containers/Profile/ProfileBankAccount/ProfileBankAccount")
);
const AccountDeactivated = lazyWithRetry(() => import("../containers/AccountDeactivated"));
const EmploymentValidation = lazyWithRetry(() => import("../containers/Boosters/Employment"));

const IncomeValidationOptions = lazyWithRetry(
  () =>
    import("../containers/Boosters/IncomeValidation/IncomeValidationOptions/ValidationOptions.jsx")
);
const BankStatementValidation = lazyWithRetry(
  () =>
    import("../containers/Boosters/IncomeValidation/BankStatementValidation/BankStatementValidation.jsx")
);
const UploadPayslips = lazyWithRetry(
  () => import("../containers/Boosters/IncomeValidation/UploadPayslips/UploadPayslips.jsx")
);
const UploadBankStatement = lazyWithRetry(
  () =>
    import("../containers/Boosters/IncomeValidation/UploadBankStatement/UploadBankStatement.jsx")
);
export default function AppRoutes() {
  const [isOnline, setIsOnline] = useState(checkNetworkStatus());

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      const status = await checkNetworkStatus();
      if (mounted) setIsOnline(status);
    };
    void init();
    const unsubscribe = subscribeToNetwork(setIsOnline);
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  return (
    <>
      {!isOnline && <NoInternet />}
      <Suspense fallback={<Loader text="Fetching your details..." />}>
        <ScrollToTop />
        <EnableGpsLocation />
        <GoToSettingModal />
        <Routes>
          {/* Public Routes */}
          <Route path={ROUTES.NotFound} element={<NotFound />} />
          <Route path={ROUTES.ROOT} element={<LaunchScreen />} />
          <Route path={ROUTES.LAUNCH} element={<LaunchScreen />} />
          <Route
            path={ROUTES.LOGIN}
            element={
              <PublicRoutes>
                <Login />
              </PublicRoutes>
            }
          />
          <Route
            path={ROUTES.LOGIN_OTP}
            element={
              <PublicRoutes>
                <LoginOTP />
              </PublicRoutes>
            }
          />
          {/* Protected Routes */}
          <Route
            path={ROUTES.PERMISSIONS}
            element={
              <ProtectedRoute>
                <Permissions />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PERMISSIONS_DECLINED}
            element={
              <ProtectedRoute>
                <PermissionsDeclined />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.HOME}
            element={
              <ProtectedRoute>
                <HomeScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ONBOARDING_HOME}
            element={
              <ProtectedRoute>
                <OnboardingHome />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.APP_INITIALIZER}
            element={
              <ProtectedRoute>
                <AppInitializer />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.REJECT}
            element={
              <ProtectedRoute>
                <RejectScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.MANUAL_REVIEW}
            element={
              <ProtectedRoute>
                <ManualReviewScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.UNIFIED_KYC_MANUAL}
            element={
              <ProtectedRoute>
                <UnifiedKycManualScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CREDIT_REPORT}
            element={
              <ProtectedRoute>
                <CreditReport />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CREDIT_REPORT_PAYMENT}
            element={
              <ProtectedRoute>
                <CreditReportPayment />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CREDIT_REPORT_PAYMENT_SUCCESS}
            element={
              <ProtectedRoute>
                <CreditReportPaymentSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CREDIT_REPORT_PAYMENT_APOLOGY}
            element={
              <ProtectedRoute>
                <CreditReportPaymentApology />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.EMAIL_VERIFICATION}
            element={
              <ProtectedRoute>
                <EmailVerification />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PERSONAL_DETAILS}
            element={
              <ProtectedRoute>
                <PersonalDetailsForm />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PAN_CHECK_PENDING}
            element={
              <ProtectedRoute>
                <PanCheckPending />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PAN_CHECK_FAILED}
            element={
              <ProtectedRoute>
                <PanCheckFailed />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.INCOME_INFO}
            element={
              <ProtectedRoute>
                <IncomeDetailsForm />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ADDRESS_DETAILS}
            element={
              <ProtectedRoute>
                <AddressDetailsForm />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ELIGIBILITY_CHECK_PENDING}
            element={
              <ProtectedRoute>
                <EligibilityCheckPending />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.REFERENCE_VERIFICATION}
            element={
              <ProtectedRoute>
                <ReferenceVerification />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.KYC_VERIFICATION}
            element={
              <ProtectedRoute>
                <KycVerification />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.VIDEO_KYC}
            element={
              <ProtectedRoute>
                <VideoKyc />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.UPLOAD_DOCUMENTS}
            element={
              <ProtectedRoute>
                <UploadDocuments />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.REUPLOAD_DOCUMENTS}
            element={
              <ProtectedRoute>
                <ReuploadDocuments />
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.KYC_CALLBACK_PENDING}
            element={
              <ProtectedRoute>
                <KycCallbackPending />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.VKYC_STATUS_PENDING}
            element={
              <ProtectedRoute>
                <VkycStatusPending />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.HANDLE_UNIFIED_KYC}
            element={
              <ProtectedRoute>
                <HandleUnifiedKYC />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.UNIFIED_KYC_WAITING}
            element={
              <ProtectedRoute>
                <UnifiedKycWaiting />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.STAGE3_BOOSTERS}
            element={
              <ProtectedRoute>
                <Stage3Boosters />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.NEXT_STEPS}
            element={
              <ProtectedRoute>
                <NextSteps />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.MOBILE_AUTH}
            element={
              <ProtectedRoute>
                <MobileAuth />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.EMPLOYMENT_VALIDATION}
            element={
              <ProtectedRoute>
                <EmploymentValidation />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.HOUSEHOLD_INCOME_DECLARATION}
            element={
              <ProtectedRoute>
                <HouseholdIncomeDeclaration />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ADDRESS_VALIDATION_STAGE4}
            element={
              <ProtectedRoute>
                <AddressValidation />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CKYC_CONSENT}
            element={
              <ProtectedRoute>
                <CkycConsent />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CKYC_DETAILS}
            element={
              <ProtectedRoute>
                <CkycDetails />
              </ProtectedRoute>
            }
          />
          {/* Stage 3 */}
          <Route
            path={ROUTES.RE_EVALUATION}
            element={
              <ProtectedRoute>
                <ReEvaluation />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CONGRATULATIONS}
            element={
              <ProtectedRoute>
                <Congratulations />
              </ProtectedRoute>
            }
          />
          {/* Stage 4 — Bank Routes */}
          <Route
            path={ROUTES.ADD_BANK_ACCOUNT}
            element={
              <ProtectedRoute>
                <AddBankAccountForm />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SELECT_BANK_ACCOUNT}
            element={
              <ProtectedRoute>
                <BankAccount />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PENNY_DROP_CHECK_PENDING}
            element={
              <ProtectedRoute>
                <PennyDropCheckPending />
              </ProtectedRoute>
            }
          />
          {/* Stage 5 — Repayment Routes */}
          <Route
            path={ROUTES.REPAYMENT_HOME}
            element={
              <ProtectedRoute>
                <RepaymentHome />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.REPAYMENT_SCHEDULE_DETAILS}
            element={
              <ProtectedRoute>
                <RepaymentScheduleDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.EMI_DETAILS}
            element={
              <ProtectedRoute>
                <EmiDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.REPAYMENT_SUCCESS}
            element={
              <ProtectedRoute>
                <RepaymentSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CLOSED_LOAN_SUCCESS}
            element={
              <ProtectedRoute>
                <ClosedLoanSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.REPAYMENT_FAILED}
            element={
              <ProtectedRoute>
                <RepaymentFailed />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PHONEPE_CALLBACK}
            element={
              <ProtectedRoute>
                <PhonePeCallback />
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.LOAN_PLAN}
            element={
              <ProtectedRoute>
                <LoanPlanPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.LOAN_SUMMARY}
            element={
              <ProtectedRoute>
                <LoanSummaryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.COUPON}
            element={
              <ProtectedRoute>
                <CouponPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.LOAN_PURPOSE}
            element={
              <ProtectedRoute>
                <LoanPurposePage />
              </ProtectedRoute>
            }
          />

          {/* Stage 6 — Loan Agreement Routes */}
          <Route
            path={ROUTES.SIGN_LOAN_AGREEMENT}
            element={
              <ProtectedRoute>
                <SignLoanAgreement />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ESIGN_AGREEMENT}
            element={
              <ProtectedRoute>
                <EsignAgreement />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ESIGN_SELFIE}
            element={
              <ProtectedRoute>
                <EsignSelfie />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.LIVE_SELFIE_CAPTURE}
            element={
              <ProtectedRoute>
                <LiveSelfieCapture />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ESIGN_FAILED}
            element={
              <ProtectedRoute>
                <EsignFailed />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ESIGN_MANUAL_VERIFICATION}
            element={
              <ProtectedRoute>
                <EsignManualVerification />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.LOAN_SANCTION_LETTER}
            element={
              <ProtectedRoute>
                <LoanSanctionLetter />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ESIGN_IN_PROGRESS}
            element={
              <ProtectedRoute>
                <EsignInProgress />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ENACH_SETUP}
            element={
              <ProtectedRoute>
                <ENachSetup />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ENACH_UPI_STEP}
            element={
              <ProtectedRoute>
                <ENachStep />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ENACH_BANK_STEP}
            element={
              <ProtectedRoute>
                <ENachStep />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.DISBURSAL_IN_PROGRESS}
            element={
              <ProtectedRoute>
                <DisbursalInProgress />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PROFILE}
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.MANAGE_PROFILE}
            element={
              <ProtectedRoute>
                <ManageProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CONTACT_US}
            element={
              <ProtectedRoute>
                <ContactUs />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.LOAN_HISTORY}
            element={
              <ProtectedRoute>
                <LoanHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.DEACTIVATE_PROFILE}
            element={
              <ProtectedRoute>
                <DeactivateProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.DEACTIVATE_PROFILE_OTP}
            element={
              <ProtectedRoute>
                <DeactivateProfileOtp />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CANT_DEACTIVATE}
            element={
              <ProtectedRoute>
                <CantDeactivate />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CANT_DELETE}
            element={
              <ProtectedRoute>
                <CantDelete />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.DELETE_PROFILE}
            element={
              <ProtectedRoute>
                <DeleteProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.DELETE_PROFILE_SUCCESS}
            element={
              <ProtectedRoute>
                <DeleteProfileSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.DOWNLOAD_LOAN_DOCUMENT}
            element={
              <ProtectedRoute>
                <DownloadLoanDocument />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.MANAGE_PERMISSIONS}
            element={
              <ProtectedRoute>
                <ManagePermissions />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PROFILE_BANK_ACCOUNT}
            element={
              <ProtectedRoute>
                <ProfileBankAccount />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ACCOUNT_DEACTIVATED}
            element={
              <ProtectedRoute>
                <AccountDeactivated />
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.INCOME_VALIDATION_OPTIONS}
            element={
              <ProtectedRoute>
                <IncomeValidationOptions />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.BANK_STATEMENT_VALIDATION}
            element={
              <ProtectedRoute>
                <BankStatementValidation />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.UPLOAD_PAYSLIPS}
            element={
              <ProtectedRoute>
                <UploadPayslips />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.UPLOAD_BANK_STATEMENT}
            element={
              <ProtectedRoute>
                <UploadBankStatement />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    </>
  );
}
